import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import {
  selectActors,
  selectAllPropPresets,
  selectSelectedTake3D,
} from "../../../app/slices/TrainingSlice";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../EnvironmentPositionContext";

const PLAYER_ID = "player";

const shortId = (id: string) => id.split("/").pop()!.slice(0, 8);

/** Display names for the ids in a take setup plan. */
export default function useTakeSetupNames() {
  const actors = useSelector(selectActors);
  const propPresets = useSelector(selectAllPropPresets);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const take = useSelector(selectSelectedTake3D);
  const { getSituationMetadata } = useSituationHelper();

  const { positionNames, positionPoints } = useMemo(() => {
    const names = new Map<string, string>();
    const points = new Map<string, { x: number; y: number }>();
    getEnvironmentPositionContexts(
      takePositions ?? [],
      take?.situations,
      getSituationMetadata,
    ).forEach((context) => {
      [
        ...context.actorPositions,
        ...context.propPositions,
        ...context.cameraPositions,
      ].forEach((position) => {
        const id = getSituationPositionId(context, position.id);
        if (position.position) {
          points.set(id, position.position);
          if (!points.has(position.id)) points.set(position.id, position.position);
        }
        if (!position.name) return;
        names.set(id, position.name);
        if (!names.has(position.id)) names.set(position.id, position.name);
      });
    });
    return { positionNames: names, positionPoints: points };
  }, [getSituationMetadata, take?.situations, takePositions]);

  /** Where a position is on the map (normalized, like the markers), if known. */
  const positionPoint = useCallback(
    (positionId: string | undefined) =>
      positionId ? positionPoints.get(positionId) : undefined,
    [positionPoints],
  );

  const actorName = useCallback(
    (actorId: string) =>
      actorId === PLAYER_ID
        ? "Player"
        : actors?.find((actor) => actor.id === actorId)?.workingName || "An actor",
    [actors],
  );
  const propName = useCallback(
    (propId: string) =>
      propPresets?.find((prop) => prop.id === propId)?.name || "A prop",
    [propPresets],
  );
  const positionName = useCallback(
    (positionId: string) => positionNames.get(positionId) ?? `Pos ${shortId(positionId)}`,
    [positionNames],
  );
  /** A look-at target is an actor, a prop or a position. */
  const targetName = useCallback(
    (targetId: string) =>
      (targetId === PLAYER_ID ? "Player" : undefined) ??
      actors?.find((actor) => actor.id === targetId)?.workingName ??
      propPresets?.find((prop) => prop.id === targetId)?.name ??
      positionName(targetId),
    [actors, propPresets, positionName],
  );

  const findActor = useCallback(
    (id: string) => actors?.find((actor) => actor.id === id),
    [actors],
  );

  return { actorName, propName, positionName, targetName, findActor, positionPoint };
}
